import type { SearchResult } from '@app/src/types/resourceExplorer'
import { Chip, Link, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'

enum CitationType {
  Text = 'Text',
  Citation = 'Citation',
}

interface ParsedTextSegment {
  type: CitationType
  content: string
  citationNumber?: number
}

const parseTextWithCitations = (text: string): ParsedTextSegment[] => {
  const citationRegex = /\[(\d+)]/g
  const matches = Array.from(text.matchAll(citationRegex))

  if (matches.length === 0) {
    return [{ type: CitationType.Text, content: text }]
  }

  return matches.flatMap((match, index) => {
    const matchStart = match.index
    const matchEnd = matchStart + match[0].length
    const previousEnd = index === 0 ? 0 : matches[index - 1].index + matches[index - 1][0].length

    const segments: ParsedTextSegment[] = []

    if (matchStart > previousEnd) {
      segments.push({
        type: CitationType.Text,
        content: text.substring(previousEnd, matchStart),
      })
    }

    segments.push({
      type: CitationType.Citation,
      content: match[0],
      citationNumber: parseInt(match[1], 10),
    })

    if (index === matches.length - 1 && matchEnd < text.length) {
      segments.push({
        type: CitationType.Text,
        content: text.substring(matchEnd),
      })
    }

    return segments
  })
}

interface SourceCitationProps {
  citationNumber: number
  searchResult: SearchResult
  onSwitchToSources: () => void
}

const SourceCitation: React.FC<SourceCitationProps> = ({
  citationNumber,
  searchResult,
  onSwitchToSources,
}: SourceCitationProps) => {
  const tooltipTitle = (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">{searchResult.title}</Typography>
      <Link href={searchResult.url} target="_blank" rel="noopener">
        <Typography variant="caption">{searchResult.url}</Typography>
      </Link>
    </Stack>
  )

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Chip
        label={citationNumber}
        size="small"
        onClick={onSwitchToSources}
        sx={{
          height: '20px',
          cursor: 'pointer',
          mx: 0.25,
          verticalAlign: 'middle',
          '&:hover': {
            backgroundColor: 'primary.main',
            color: 'white',
          },
        }}
      />
    </Tooltip>
  )
}

interface TextWithCitationsProps {
  text: string
  searchResults: SearchResult[]
  onSwitchToSources: () => void
}

export const TextWithCitations: React.FC<TextWithCitationsProps> = ({
  text,
  searchResults,
  onSwitchToSources,
}: TextWithCitationsProps) => {
  const segments = parseTextWithCitations(text)

  return (
    <Typography>
      {segments.map(segment => {
        if (segment.type === CitationType.Text) {
          return <React.Fragment key={`${segment.type}-${segment.content}`}>{segment.content}</React.Fragment>
        }

        if (segment.type === CitationType.Citation && segment.citationNumber) {
          const searchResult = searchResults[segment.citationNumber - 1]
          return (
            <SourceCitation
              key={`${segment.type}-${segment.citationNumber}`}
              citationNumber={segment.citationNumber}
              searchResult={searchResult}
              onSwitchToSources={onSwitchToSources}
            />
          )
        }

        return null
      })}
    </Typography>
  )
}
